<template>
  <div class="tool-page">
    <!-- 面包屑导航 -->
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('timestamp.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('timestamp.title') }}</h1>
      <p>{{ $t('timestamp.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="current-time">
        <div class="time-label">{{ $t('timestamp.currentTimestamp') }}</div>
        <div class="time-value">{{ currentTimestamp }}</div>
        <button class="btn small" @click="copyTimestamp">{{ $t('common.copy') }}</button>
      </div>

      <div class="converter-section">
        <h3>{{ $t('timestamp.toDate') }}</h3>
        <div class="input-group">
          <input
            v-model="timestampInput"
            type="text"
            :placeholder="$t('timestamp.inputPlaceholder')"
            @input="convertTimestampToDate"
          />
          <select v-model="timestampUnit" @change="convertTimestampToDate">
            <option value="s">{{ $t('timestamp.seconds') }}</option>
            <option value="ms">{{ $t('timestamp.milliseconds') }}</option>
          </select>
        </div>
        <div class="result-box" v-if="dateResult">
          <div class="result-row">
            <span class="label">{{ $t('timestamp.localTime') }}:</span>
            <span class="value">{{ dateResult.local }}</span>
          </div>
          <div class="result-row">
            <span class="label">{{ $t('timestamp.utcTime') }}:</span>
            <span class="value">{{ dateResult.utc }}</span>
          </div>
          <div class="result-row">
            <span class="label">{{ $t('timestamp.isoFormat') }}:</span>
            <span class="value">{{ dateResult.iso }}</span>
          </div>
        </div>
        <div v-if="timestampError" class="alert error">{{ timestampError }}</div>
      </div>

      <div class="converter-section">
        <h3>{{ $t('timestamp.toTimestamp') }}</h3>
        <div class="date-inputs">
          <div class="input-group">
            <input
              v-model="dateInput"
              type="datetime-local"
              @input="convertDateToTimestamp"
            />
            <button class="btn" @click="setNow">{{ $t('timestamp.currentTime') }}</button>
          </div>
        </div>
        <div class="result-box" v-if="timestampResult">
          <div class="result-row">
            <span class="label">{{ $t('timestamp.secondsTimestamp') }}:</span>
            <span class="value">{{ timestampResult.seconds }}</span>
            <button class="btn small" @click="copyValue(timestampResult.seconds)">{{ $t('common.copy') }}</button>
          </div>
          <div class="result-row">
            <span class="label">{{ $t('timestamp.millisecondsTimestamp') }}:</span>
            <span class="value">{{ timestampResult.milliseconds }}</span>
            <button class="btn small" @click="copyValue(timestampResult.milliseconds)">{{ $t('common.copy') }}</button>
          </div>
        </div>
      </div>

      <div v-if="copyMessage" class="copy-toast">{{ copyMessage }}</div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('timestamp.whatIsTimestamp') }}</h2>
      <p>{{ $t('timestamp.whatIsTimestampDesc') }}</p>
      
      <h2>{{ $t('timestamp.useCases') }}</h2>
      <ul>
        <li><strong>{{ $t('timestamp.useCaseDb') }}</strong></li>
        <li><strong>{{ $t('timestamp.useCaseApi') }}</strong></li>
        <li><strong>{{ $t('timestamp.useCaseLog') }}</strong></li>
        <li><strong>{{ $t('timestamp.useCaseCache') }}</strong></li>
      </ul>

      <h2>{{ $t('timestamp.comparison') }}</h2>
      <p>{{ $t('timestamp.comparisonDesc') }}</p>
      <ul>
        <li><strong>{{ $t('timestamp.comparisonSeconds') }}</strong></li>
        <li><strong>{{ $t('timestamp.comparisonMilliseconds') }}</strong></li>
      </ul>

      <h2>{{ $t('timestamp.timestampHistory') }}</h2>
      <p>{{ $t('timestamp.timestampHistoryDesc') }}</p>

      <h2>{{ $t('timestamp.y2038Problem') }}</h2>
      <p>{{ $t('timestamp.y2038ProblemDesc') }}</p>

      <h2>{{ $t('timestamp.timezoneExplain') }}</h2>
      <p>{{ $t('timestamp.timezoneExplainDesc') }}</p>

      <h2>{{ $t('timestamp.commonTimestamps') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('timestamp.commonTimestampsItems')" :key="'ts'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('timestamp.timestampTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('timestamp.timestampTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('timestamp.programmingExamples') }}</h2>
      <ul>
        <li><strong>JavaScript：</strong>Date.now() or new Date().getTime() (ms)</li>
        <li><strong>Python：</strong>import time; time.time() (s, float)</li>
        <li><strong>Java：</strong>System.currentTimeMillis() (ms)</li>
        <li><strong>PHP：</strong>time() (s)</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimestampConverter',
  data() {
    return {
      currentTimestamp: '',
      timestampInput: '',
      timestampUnit: 's',
      dateInput: '',
      dateResult: null,
      timestampResult: null,
      timestampError: '',
      copyMessage: '',
      timer: null
    }
  },
  mounted() {
    this.updateCurrentTimestamp()
    this.timer = setInterval(this.updateCurrentTimestamp, 1000)
    this.setNow()
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    updateCurrentTimestamp() {
      this.currentTimestamp = Math.floor(Date.now() / 1000)
    },

    convertTimestampToDate() {
      this.timestampError = ''
      this.dateResult = null

      if (!this.timestampInput.trim()) return

      const ts = parseInt(this.timestampInput.trim())
      if (isNaN(ts)) {
        this.timestampError = this.$t('timestamp.invalidNumber')
        return
      }

      try {
        const milliseconds = this.timestampUnit === 's' ? ts * 1000 : ts
        const date = new Date(milliseconds)

        if (isNaN(date.getTime())) {
          this.timestampError = this.$t('timestamp.invalidTimestamp')
          return
        }

        this.dateResult = {
          local: date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }),
          utc: date.toUTCString(),
          iso: date.toISOString()
        }
      } catch (e) {
        this.timestampError = this.$t('timestamp.conversionFailed')
      }
    },

    convertDateToTimestamp() {
      if (!this.dateInput) {
        this.timestampResult = null
        return
      }

      try {
        const date = new Date(this.dateInput)
        const ms = date.getTime()
        
        this.timestampResult = {
          seconds: Math.floor(ms / 1000),
          milliseconds: ms
        }
      } catch (e) {
        this.timestampResult = null
      }
    },

    setNow() {
      const now = new Date()
      const offset = now.getTimezoneOffset() * 60000
      const localISOTime = new Date(now - offset).toISOString().slice(0, 16)
      this.dateInput = localISOTime
      this.convertDateToTimestamp()
    },

    async copyTimestamp() {
      await this.copyValue(this.currentTimestamp)
    },

    async copyValue(value) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(String(value))
        } else {
          this.fallbackCopy(String(value))
          return
        }
        this.showCopyMessage(this.$t('common.copied'))
      } catch (e) {
        this.fallbackCopy(String(value))
      }
    },

    fallbackCopy(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        this.showCopyMessage(this.$t('common.copied'))
      } catch (e) {
        this.showCopyMessage(this.$t('common.copyFailed'))
      }
      document.body.removeChild(textArea)
    },

    showCopyMessage(message) {
      this.copyMessage = message
      setTimeout(() => {
        this.copyMessage = ''
      }, 2000)
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 800px;
  margin: 0 auto;
}

/* 面包屑导航 */
.breadcrumb {
  margin-bottom: 24px;
  font-size: 14px;
  color: var(--text-secondary);
}

.breadcrumb a {
  color: var(--accent-color);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb .separator {
  margin: 0 8px;
  color: var(--text-muted);
}

.breadcrumb .current {
  color: var(--text-secondary);
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  transition: background 0.3s, border-color 0.3s;
}

.current-time {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--accent-light);
  border: 1px solid var(--accent-color);
  border-radius: 12px;
  margin-bottom: 24px;
}

.time-label {
  color: var(--text-secondary);
  font-size: 14px;
}

.time-value {
  font-size: 28px;
  font-weight: 600;
  font-family: 'Monaco', 'Menlo', monospace;
  color: var(--accent-color);
}

.converter-section {
  margin-bottom: 32px;
}

.converter-section h3 {
  font-size: 18px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.input-group {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.input-group input {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 16px;
  transition: background 0.3s, border-color 0.3s, color 0.3s;
}

.input-group input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.input-group select {
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.3s, border-color 0.3s, color 0.3s;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn:hover {
  background: var(--bg-hover);
}

.btn.small {
  padding: 6px 12px;
  font-size: 12px;
}

.result-box {
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  transition: background 0.3s, border-color 0.3s;
}

.result-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light);
}

.result-row:last-child {
  border-bottom: none;
}

.result-row .label {
  color: var(--text-secondary);
  font-size: 14px;
  min-width: 100px;
}

.result-row .value {
  flex: 1;
  font-family: 'Monaco', 'Menlo', monospace;
  color: var(--text-primary);
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 12px;
}

.alert.error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: var(--error-color);
}

.copy-toast {
  position: fixed;
  top: 80px;
  right: 20px;
  background: var(--success-color);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .current-time {
    flex-direction: column;
    text-align: center;
  }

  .input-group {
    flex-direction: column;
  }

  .result-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* 工具说明 */
.tool-description {
  margin-top: 32px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 24px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--accent-light);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 16px;
}

.tool-description ul,
.tool-description ol {
  padding-left: 24px;
  margin-bottom: 16px;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 8px;
}

.tool-description strong {
  color: var(--text-primary);
}
</style>
